import React, { FC, useEffect, useRef } from 'react';

import { useNavigate } from 'react-router';

import { LoyaltyFab } from 'components/layout/fab-container/loyalty-fab';
import { LoadingAnimation } from 'components/loading-animation';
import { Modal } from 'components/modal';
import { LOADING_ANIMATION_AS_REFRESH_CONTENT } from 'components/offers/ui-refresh/constants';
import { MobileBackButton } from 'components/offers/ui-refresh/mobile-back-button';
import { useEnablePullToReloadOnScroll } from 'components/pull-to-reload';
import { MediaQuery, useMediaQuery } from 'hooks/use-media-query';
import { OffersIncentiveDetailsSkeleton } from 'pages/loyalty/loyalty-incentives-components/incentive-details/incentive-details-skeleton';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';

import { LoyaltyOffersCooldown } from '../loyalty-offers/loyalty-offers-cooldown';
import { LoyaltyPullToReload } from '../loyalty-pull-to-reload';

import { ExternalIncentiveDetailsMemo } from './external-incentive-details/external-incentive-details';
import { IncentiveDetailsMemo } from './incentive-details';
import { LoyaltyFabContainer } from './incentive-details/incentive-details.styled';
import {
  IncentiveDetailsWrapper,
  IncentiveList,
  IncentivesGrid,
  LoadingAnimationContainer,
} from './loyalty-incentives.styled';

export const LoyaltyIncentivesGrid: FC<any> = ({
  selectedIncentive,
  engineIncentivesMap,
  goBack,
  children,
  refetch,
  setScrollContainerRef,
  offersInCooldown = false,
  isWidget,
  isCarousel,
  setSelectedRewardIdFromWidget,
  challengeIncentiveSelected,
  offersLoadingState,
}) => {
  const isIncentiveSelected = Boolean(selectedIncentive);
  const isExternalOffer = selectedIncentive?.__typename === 'ExternalOffers';

  const { getScrollPositionForReload, pullToReloadEnabled, setScrollRef } =
    useEnablePullToReloadOnScroll();
  const isMobile = useMediaQuery(MediaQuery.Mobile);
  const navigate = useNavigate();
  const { isDrawerOpen } = useInRestaurantRedemptionContext();

  const incentiveDetailsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isIncentiveSelected) {
      // We need to scroll the incentive details container back into view
      // because sometimes the incentive list is long and if you click on
      // an offer from the lower end of the page the details do not show up
      incentiveDetailsRef.current?.scrollIntoView();
    }
  }, [isIncentiveSelected]);

  const shouldShowLoaderIncentive = offersLoadingState && !selectedIncentive;

  return (
    <>
      {isMobile && isIncentiveSelected && <MobileBackButton to={goBack} />}
      <IncentivesGrid
        incentiveSelected={isIncentiveSelected}
        isWidget={isWidget}
        isCarousel={isCarousel}
      >
        <IncentiveList
          isIncentiveSelected={isIncentiveSelected}
          data-testid="incentives-list"
          onScroll={getScrollPositionForReload}
          ref={el => {
            if (setScrollContainerRef) {
              setScrollContainerRef(el);
            }
            setScrollRef(el);
          }}
        >
          {offersInCooldown && <LoyaltyOffersCooldown />}
          {/* If it's a widget, don't allow to pull on reload */}
          {isWidget ? (
            children
          ) : (
            <LoyaltyPullToReload
              onRefresh={refetch}
              enabled={pullToReloadEnabled}
              refreshContent={
                LOADING_ANIMATION_AS_REFRESH_CONTENT ? (
                  <LoadingAnimationContainer>
                    <LoadingAnimation fillColor={Styles.color.primary} />
                  </LoadingAnimationContainer>
                ) : undefined
              }
            >
              {children}
            </LoyaltyPullToReload>
          )}
        </IncentiveList>
        {selectedIncentive &&
          !isExternalOffer &&
          (isWidget ? (
            <>
              {!isDrawerOpen ? (
                <Modal
                  onDismiss={() => {
                    setSelectedRewardIdFromWidget('');
                    navigate(goBack);
                  }}
                  eventData={{
                    modalAppearanceEventMessage: 'Show reward modal',
                  }}
                  backgroundColor={Styles.color.white}
                >
                  <IncentiveDetailsMemo
                    engineIncentivesMap={engineIncentivesMap}
                    selectedIncentive={selectedIncentive}
                    isWidget
                  />
                  <LoyaltyFabContainer>
                    <LoyaltyFab />
                  </LoyaltyFabContainer>
                </Modal>
              ) : null}
            </>
          ) : (
            <IncentiveDetailsWrapper
              data-testid="incentive-details-container"
              ref={incentiveDetailsRef}
            >
              <IncentiveDetailsMemo
                engineIncentivesMap={engineIncentivesMap}
                selectedIncentive={selectedIncentive}
                challengeIncentiveSelected={challengeIncentiveSelected}
                onGoBack={() => {
                  setSelectedRewardIdFromWidget('');
                  navigate(goBack);
                }}
              />
            </IncentiveDetailsWrapper>
          ))}
        {!shouldShowLoaderIncentive &&
          isExternalOffer &&
          (isWidget ? (
            <Modal
              onDismiss={() => {
                setSelectedRewardIdFromWidget('');
                navigate(goBack);
              }}
              eventData={{
                modalAppearanceEventMessage: 'Show reward modal',
              }}
              backgroundColor={Styles.color.white}
            >
              <ExternalIncentiveDetailsMemo selectedIncentive={selectedIncentive} />
            </Modal>
          ) : (
            <IncentiveDetailsWrapper
              data-testid="incentive-details-container"
              ref={incentiveDetailsRef}
            >
              <ExternalIncentiveDetailsMemo selectedIncentive={selectedIncentive} />
            </IncentiveDetailsWrapper>
          ))}
        {!isMobile && shouldShowLoaderIncentive && <OffersIncentiveDetailsSkeleton />}
      </IncentivesGrid>
    </>
  );
};
